import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import type { SEOHandle } from '@nasa-gcn/remix-seo'
import { animated, useSpring } from '@react-spring/web'
import { useState } from 'react'
import { Form, Link, data, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { AuthFormWrapper } from '#app/components/auth/AuthFormWrapper'
import { AuthHeader } from '#app/components/auth/AuthHeader'
import { AuthLayout } from '#app/components/auth/AuthLayout'
import { AuthProviders } from '#app/components/auth/AuthProviders'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import {
	CheckboxField,
	ErrorList,
	FloatingLabelField,
} from '#app/components/forms'
import { AnimatedContainer } from '#app/components/ui/animated-container'
import { AnimatedStatusButton } from '#app/components/ui/animated-status-button'
import { StaggerChildren } from '#app/components/ui/stagger-children'
import { smoothSpring } from '#app/utils/animations'
import { getAuthConfig } from '#app/utils/auth-config.server'
import { login, requireAnonymous } from '#app/utils/auth.server'
import { checkHoneypot } from '#app/utils/honeypot.server'
import { useIsPending } from '#app/utils/misc'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation'
import type { Route } from './+types/tenant.login'
import { handleNewSession } from './login.server'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	username: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
	termsAccepted: z.union([z.literal('on'), z.literal(true)], {
		required_error: 'You must agree to the Terms & Privacy to continue',
		invalid_type_error: 'You must agree to the Terms & Privacy to continue',
	}),
})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	const authConfig = getAuthConfig('tenant')
	const url = new URL(request.url)
	const invitation = url.searchParams.get('invitation')
	return { authConfig, invitation }
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				try {
					const session = await login({
						...data,
						expectedUserType: 'tenant',
					})
					if (!session) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Invalid email or password',
						})
						return z.NEVER
					}

					return { ...data, session }
				} catch (error) {
					// Handle userType mismatch error
					if (error instanceof Error && error.message.includes('user type')) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message:
								'This account is registered as a landlord. Please use the landlord login page.',
						})
						return z.NEVER
					}
					throw error
				}
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{
				result: submission.reply({ hideFields: ['password', 'termsAccepted'] }),
			},
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	// Check for invitation parameter in form data or URL
	const invitationToken = formData.get('invitation')?.toString()

	// If invitation exists, redirect to invitation route instead of default redirectTo
	const finalRedirectTo = invitationToken
		? `/tenant/screening-invitation/${invitationToken}`
		: redirectTo

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo: finalRedirectTo,
	})
}

export default function TenantLoginPage({
	actionData,
	loaderData,
}: Route.ComponentProps) {
	const [showTermsModal, setShowTermsModal] = useState(false)
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const authConfig = loaderData.authConfig
	const invitation = loaderData.invitation

	// Build signup link preserving redirectTo and invitation params
	const signupParams = new URLSearchParams()
	if (redirectTo) signupParams.set('redirectTo', redirectTo)
	if (invitation) signupParams.set('invitation', invitation)
	const signupLink = signupParams.toString()
		? `${authConfig.signupPath}?${signupParams.toString()}`
		: authConfig.signupPath

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	// React Spring animation for error messages
	const hasErrors = Boolean(form.errors?.length)
	const errorSpring = useSpring({
		opacity: hasErrors ? 1 : 0,
		y: hasErrors ? 0 : -10,
		config: smoothSpring,
	})

	return (
		<AuthLayout
			backgroundImage={authConfig.backgroundImage}
			heroText={authConfig.heroText}
			userType="tenant"
		>
			<AuthHeader />
			<AuthFormWrapper
				title={authConfig.titles.login}
				subtitle={authConfig.subtitles.login}
				titleClassName="text-[#32302F] text-[22px] md:text-[35px] capitalize font-medium"
				subtitleClassName="text-[#747474] font-400 text-[16px] md:text-[20px] "
			>
				<AnimatedContainer>
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<StaggerChildren>
							<FloatingLabelField
								inputProps={{
									...getInputProps(fields.username, { type: 'email' }),
									autoComplete: 'email',
									placeholder: 'Enter Your Email Address',
									className:
										'placeholder:text-[#CCCCCC]  placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]',
								}}
								labelProps={{
									children: 'Email Address',
									htmlFor: fields.username.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass={'min-h-auto  md:px-0 md:pt-0 md:pb-0 '}
								className="mb-[20px]"
								errors={fields.username.errors}
							/>
							<FloatingLabelField
								passwordHide
								inputProps={{
									...getInputProps(fields.password, { type: 'password' }),
									autoComplete: 'password',
									placeholder: 'Enter Password',
									className:
										'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] border border-[#CCCCCC] rounded-[30px] text-[#484848]',
								}}
								labelProps={{
									children: 'Password',
									htmlFor: fields.password.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass={'min-h-auto md:px-0 md:pt-0'}
								errors={fields.password.errors}
							/>
							<div className="mt-[14px] text-[#32302F]">
								<Link
									to={authConfig.forgotPasswordPath}
									className="text-[14px] font-medium text-[#32302F]"
								>
									Forgot Password?
								</Link>
							</div>
							<CheckboxField
								className="mt-[30px]"
								labelProps={{
									className: 'text-[14px] text-[#32302F] cursor-pointer',
									children: (
										<>
											I agree to the{' '}
											<Link
												to="/terms"
												target="_blank"
												className="font-medium underline"
											>
												Terms & Privacy
											</Link>
										</>
									),
								}}
								buttonProps={{
									...getInputProps(fields.termsAccepted, {
										type: 'checkbox',
										value: 'on',
									}),
									className:
										'h-[30px] w-[30px] rounded-[10px] border-[#CCCCCC]',
								}}
								errors={fields.termsAccepted.errors}
							/>

							<animated.div style={errorSpring} className="mt-6 mb-2">
								<ErrorList errors={form.errors} id={form.errorId} />
							</animated.div>
							<AnimatedStatusButton
								className="mt-[50px] mb-[30px] h-[55px] w-full rounded-[30px] border-0 bg-[#32302F] py-[18px] text-[20px] text-[#FFFFFF] capitalize transition-colors duration-200 hover:bg-[#1a1918] hover:text-[#FFFFFF] active:bg-[#0f0f0e] md:py-[23px]"
								status={isPending ? 'pending' : (form.status ?? 'idle')}
								type="submit"
								disabled={isPending}
								variant={'outline'}
							>
								log in
							</AnimatedStatusButton>
							<p className="mt-8 mb-6 text-center text-[14px] font-normal text-[#32302F] md:text-[16px]">
								Don't have an account?
								<span
									className="text-[14px] font-normal text-[#32302F] md:text-[16px]"
									aria-hidden
								>
									{/* | */}
								</span>{' '}
								<Link
									to={signupLink}
									className="text-md font-500 text-[14px] font-medium text-black underline md:text-[16px]"
								>
									Create an Account
								</Link>
							</p>
						</StaggerChildren>
					</Form>
					{showTermsModal && (
						<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
							<div className="w-[90%] max-w-[900px] max-h-[90vh] rounded-[20px] bg-white p-6 shadow-lg">
								<h2 className="mb-4 text-[22px] font-semibold text-[#32302F]">
									Terms & Conditions
								</h2>

								<div className="max-h-[50vh] overflow-y-auto text-[14px] text-[#555]">
									<p className="mb-4">
										The TrueTenant Inc. Terms of Service, including all sections
										below, (hereinafter, collectively, the “Agreement”)
										constitute the entire agreement between You and TrueTenant
										Inc. regarding the access to and use of the TrueTenant
										Platform, including our website, mobile application,
										Application Programming Interfaces (APIs), and any
										screening, marketplace, leasing, or payment-related services
										offered through it. This Agreement also applies to the
										ordering, purchasing, and/or receiving of paid
										tenant-screening products, subscription plans, or other
										services we provide.
									</p>
									<p className="mb-4">
										You must accept the terms of this Agreement before you may
										register for, purchase, or use any Services offered by
										TrueTenant. By creating an account, submitting a screening
										request, purchasing a report, or accessing any portion of
										the Platform, you affirm that you have read, understood, and
										agree to be bound by the then-current version of this
										Agreement. It is your responsibility to review this
										Agreement periodically to ensure you are aware of any
										updates. Where required by law, TrueTenant will provide
										advance notice of material changes.
									</p>
									<p className="mb-4">
										In this Agreement, “TrueTenant Inc.”, “TrueTenant,” “we,”
										“us,” or “our” refers to TrueTenant Inc., an Ontario
										corporation with its principal office at 713 Indian Road,
										Mississauga, Ontario, Canada.“You” or “User” refers to any
										individual or entity accessing or using the Platform,
										including tenants, landlords, property managers, real estate
										agents, or authorized organizational representatives.
										TrueTenant works with certain third-party service providers
										(“Service Providers”) to deliver identity verification,
										financial-data aggregation, rental-history retrieval, AI
										analysis, payment processing, and hosting functions
										necessary to operate the Platform.
									</p>

									<h4 className="text-[18px] mt-4 font-[600] text-[#343434] mb-4">
										General Website and User Terms (TrueTenant Version)
									</h4>
									<p className="mb-4">
										This section applies when You visit the TrueTenant website
										(‘Site’), mobile application, APIs, or any associated pages,
										subpages, sections, microsites, or integrated services
										(collectively, the “Platform”).
									</p>

									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.1 Governing Law and Jurisdiction:
									</h5>
									<p className="mb-4">
										Access to, and use of, the Platform are subject to all laws
										and regulations applicable in your province or territory of
										residence. Individuals residing in jurisdictions where
										access to or use of the Platform is illegal or prohibited
										are not permitted to use the Platform. It is your
										responsibility to determine whether you may legally use the
										Platform in compliance with applicable laws.
									</p>
									<p className="mb-4">
										Certain content, products, services, listings, and/or
										features may not be offered, be applicable or available to
										You based on residency, age or other eligibility criteria or
										factors. Persons accessing the Platform are required to
										obtain information about and observe any relevant
										restrictions. If any term or condition in this Agreement is
										found to be unlawful, void, or for any reason unenforceable,
										then that provision shall be deemed to be severable from the
										rest of the terms and conditions and shall not affect the
										validity and enforceability of any remaining provisions of
										the Agreement. The terms, exclusions and conditions of the
										written contracts relating to content or products mentioned
										on this Site are not fully described on the Site. Their
										interpretation is governed by the terms and conditions of
										the written contract You enter with Us, and by any
										applicable laws and regulations. The Agreement is made and
										will be interpreted under Ontario law, and you submit to the
										exclusive jurisdiction of Ontario courts located in Toronto.{' '}
									</p>

									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.2.No Guarantee of Availability
									</h5>

									<p className="mb-4">
										THE PLATFORM AND ALL SERVICES ARE PROVIDED ON AN “AS IS” AND
										“AS AVAILABLE” BASIS, WITHOUT WARRANTIES OF ANY KIND,
										EXPRESS OR IMPLIED.
									</p>
									<p className="mb-4">
										Given the nature of online systems and third-party
										integrations (including banking APIs, identity-verification
										services, and payment processors), TrueTenant does not
										represent or warrant that the Platform or its features will
										be uninterrupted, error-free, secure, or free of viruses,
										malicious code, or other harmful components.
									</p>

									<p className="mb-4">
										Although we use reasonable efforts to maintain functionality
										and uptime, you acknowledge that interruptions, delays, or
										technical issues may occur for various reasons, including
										external service provider outages. We are not liable for any
										such issues.
									</p>
									<p className="mb-4">
										You agree to promptly notify us of any system errors. In the
										event of a system failure that prevents a paid screening or
										service from being delivered, your sole and exclusive remedy
										is a refund of the applicable fee you paid for that specific
										service.
									</p>

									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.3 Product Modification or Termination
									</h5>
									<p className="mb-4">
										TrueTenant may modify, suspend, or discontinue any feature,
										screening service, subscription tier, or portion of the
										Platform at any time, with or without notice. We may also
										cancel, suspend, or terminate this Agreement for any reason.
										If we discontinue a paid service that you have purchased but
										not yet received, you will receive a pro-rated refund.
										Notice of such changes may be provided by email, through
										your account dashboard, or directly on the Platform.
									</p>
									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.4 Disclaimers and Limitation of Liability
									</h5>
									<p className="mb-4">
										TrueTenant does not control or independently verify all
										information provided by Users or by third-party data sources
										(including Flinks, TenCheck, Verrif, OpenRoom, or hosting
										partners). Screening outcomes, eligibility results, and the
										TrueTenant Score depend on the accuracy and completeness of
										the information submitted by applicants and third-party
										providers.
									</p>
									<p className="mb-4">
										We make no representations, warranties, or guarantees except
										those expressly stated in this Agreement. We specifically do
										not guarantee the predictive value, accuracy, or outcome of
										any screening report, TrueTenant Score, or rental
										application assessment. TrueTenant is not liable for any
										decisions made by landlords, property managers, or tenants
										using the Platform
									</p>
									<p className="mb-4">
										To the fullest extent permitted by law, TrueTenant disclaims
										all liability arising from your access to or use of the
										Platform or Services. Under no circumstances will
										TrueTenant, its affiliates, officers, employees, licensors,
										or service providers be liable for any direct, indirect,
										incidental, consequential, special, exemplary, or punitive
										damages, including loss of revenue, rental income, property
										damage, or issues arising from taxes charged or remitted in
										connection with Services.
									</p>

									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.5 Electronic Communications
									</h5>
									<p className="mb-4">
										Unless otherwise noted, all products and services offered
										through the TrueTenant Platform are internet-based and
										require that you maintain:
									</p>

									<ul className="list-disc pl-5">
										<li>
											an internet browser capable of supporting modern
											encryption standards,
										</li>
										<li>
											an active email account and compatible email software,
										</li>
										<li>
											a computer or mobile device with a reliable connection to
											the internet, and
										</li>
										<li>
											sufficient storage capacity or a printer to save or print
											electronic records.
										</li>
									</ul>
									<p className="mb-4">
										You understand and agree that this Agreement is entered into
										electronically, and that all notices, disclosures,
										agreements, screening results, consent forms, invoices, and
										other communications (“Communications”) will be provided to
										you electronically—either through this Agreement, your
										email, your TrueTenant account dashboard, or notices posted
										on the Platform.
									</p>
									<p className="mb-4">
										By using the Platform, you expressly consent to receive
										Communications electronically and agree that electronic
										delivery satisfies any legal requirement that such
										Communications be provided in writing.
									</p>
									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.6 Ownership and Intellectual Property
									</h5>
									<p className="mb-4">
										TrueTenant is the owner and/or authorized licensor of the
										Platform, its software, algorithms (including the TrueTenant
										Score), screening tools, design elements, documentation,
										text, graphics, databases, and all other content made
										available through the Platform (“TrueTenant Content”).
									</p>
									<p className="mb-4">
										Where you access the Platform on behalf of a business (such
										as a property-management company), your use is limited to
										internal business purposes related to rental screening,
										listing management, and other property-related functions. If
										you access the Platform as an individual consumer, your use
										is limited to personal purposes related to rental
										applications and tenant-screening services.
									</p>
									<p className="mb-4">
										You acknowledge that all intellectual property rights remain
										vested exclusively in TrueTenant and its licensors, and you
										acquire no ownership interest in any portion of the
										Platform. You agree that you will not, except as expressly
										permitted in this Agreement, attempt to do, or omit to do,
										anything which is likely to prejudice TrueTenant’s ownership
										(or its licensors’ ownership) of its intellectual property
										rights, including:{' '}
									</p>
									<ul className="list-disc pl-5">
										<li>
											access, copy, store, merge, aggregate, scrape, or compile
											Platform content;
										</li>
										<li>
											modify, adapt, decompile, disassemble, or reverse-engineer
											the Platform or any component;
										</li>
										<li>
											derive or attempt to derive the source code, algorithms,
											scoring logic, or system architecture;
										</li>
										<li>create derivative works from the Platform;</li>
										<li>
											sublicense, sell, distribute, or commercially exploit any
											part of the Platform or Services; or
										</li>
										<li>
											circumvent, bypass, or interfere with security features,
											access controls, or rate-limiting systems.
										</li>
									</ul>
									<p className="mb-4">
										Any violation of these restrictions may result in suspension
										or termination of your account and may expose you to legal
										liability.
									</p>

									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.6.1. Property Listings and User-Generated Content
									</h5>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.1.1. User Content Ownership:
										</b>{' '}
										Users retain ownership of any photos, descriptions,
										documents, messages, listing information, profile data, or
										other materials (“User Content”) that they upload, submit,
										or transmit through the Platform.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.1.2. License to TrueTenant:{' '}
										</b>{' '}
										By uploading or submitting User Content, you grant
										TrueTenant a non-exclusive, worldwide, royalty-free,
										transferable licence to host, store, reproduce, display,
										distribute, and otherwise use such User Content as necessary
										to operate, maintain, market, and improve the Platform and
										related Services.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.1.3. Representations:{' '}
										</b>{' '}
										You represent and warrant that: (a) you have all necessary
										rights and permissions to upload and use the User Content;
										(b) the User Content is accurate, lawful, and not
										misleading; (c) the User Content does not infringe any
										intellectual property, privacy, or proprietary rights of any
										third party; and (d) the User Content complies with all
										applicable laws, including housing, privacy, and
										human-rights legislation.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.1.4. Property Listings:
										</b>{' '}
										Landlords and Property Managers are solely responsible for
										ensuring the accuracy, legality, and completeness of all
										property listings, including photos, pricing, amenities,
										availability, and descriptions. TrueTenant does not verify
										or guarantee the accuracy, safety, condition, or suitability
										of any property.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.1.5. Right to Remove or Modify Content:
										</b>{' '}
										TrueTenant may remove, disable access to, or request
										modification of any User Content that violates this
										Agreement, applicable law, or TrueTenant policies, or that
										TrueTenant determines, in its sole discretion, to be
										harmful, fraudulent, misleading, or inappropriate.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.1.6. No Liability for User Content:
										</b>{' '}
										TrueTenant does not control, verify, or endorse User Content
										and is not responsible for any errors, omissions,
										inaccuracies, or resulting disputes between Users.
									</p>

									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.6.2. User Conduct
									</h5>
									<p className="mb-4">
										Users agree not to engage in any of the following conduct
										when accessing or using the Platform:
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.2.1. Harassment or Discrimination:{' '}
										</b>{' '}
										Engaging in abusive, threatening, hateful, discriminatory,
										or harassing behaviour toward any User, landlord, tenant,
										property manager, or TrueTenant representative.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.2.2. Uploading Harmful or Offensive Content:
										</b>{' '}
										Uploading, posting, or transmitting any content that is
										defamatory, obscene, hateful, violent, or otherwise
										inappropriate or disruptive to the Platform.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.2.3. Security Interference:{' '}
										</b>{' '}
										Attempting to damage, disrupt, or interfere with Platform
										operations, including: (a) uploading malware or harmful
										code, (b) attempting unauthorized access to accounts,
										systems, APIs, or data, or (c) bypassing security controls,
										authentication measures, or rate limits.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.2.4. Unauthorized Commercial Use:
										</b>{' '}
										Using the Platform or its Services to solicit, advertise,
										run promotions, or conduct unrelated commercial activity
										without TrueTenant’s written approval.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.2.5. Improper Use of Other Users’ Information
										</b>{' '}
										Accessing, collecting, storing, or using personal
										information of other Users (including tenants or landlords)
										for any purpose not expressly authorized by this Agreement
										or by the User whose information is involved.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											1.6.2.6. Interference With Property Listings or
											Marketplace Integrity
										</b>{' '}
										Manipulating property listings, reviews, communications, or
										application workflows in ways that mislead or harm other
										Users or undermine the integrity of the Platform.
									</p>

									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.7 Security, Cookies, and Links
									</h5>
									<p className="mb-4">
										Once You have gained access to the TrueTenant Site, it is
										highly recommended that You do not leave the computer
										terminal until You have left the Site. We do not warrant
										that any information, software or other material accessible
										through the Site or through links to other websites are free
										of viruses, worms, Trojan horses or other harmful
										components. Internet-based communication—including
										submission of documents, identity verification, and data
										transmission—may not be fully secure without encryption, and
										privacy cannot be absolutely guaranteed. Without the use of
										encryption, communication through the Internet or electronic
										communication is not secure and privacy cannot be ensured.
										We do not review or approve outside sites that are or may be
										accessible through the platform. Links from or to Internet
										websites outside the Site are meant for convenience only and
										you access them at Your own risk.{' '}
									</p>
									<p className="mb-4">
										These links should not be construed as an endorsement, a
										guarantee or a recommendation of the products or services
										offered on those sites. We expressly disclaim any liability
										of any nature whatsoever arising directly or indirectly from
										other sites connected to the Site by hyperlink or otherwise
										linked to TrueTenant’s platform. Additional information
										about cookies, pixel tags, and other technology used on the
										Site can be found in our Privacy Policy.
									</p>

									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]">
										1.8 Waiver, Severability, and Assignment
									</h5>
									<p className="mb-4">
										TrueTenant’s waiver of a breach of any provision of this
										Agreement will not operate or be interpreted as a waiver of
										any other or subsequent breach. If any part of this
										Agreement is held to be invalid or unenforceable, that part
										will be severed and the rest of the Agreement will remain in
										force. You may not transfer or assign any part of this
										Agreement. We may assign this Agreement without restriction.
									</p>

									<h4 className="text-[18px] mt-4 font-[600] text-[#343434] mb-4">
										2. Specific Additional Terms for Screening, Verification,
										and Tenant-Facing Services
									</h4>
									<p className="mb-4">
										This section applies in addition to Section 1 when you
										create a TrueTenant account as a consumer and order or
										receive tenant-screening, identity-verification,
										rental-readiness, rental-application services or property
										listing services (collectively, “Screening Services”).
									</p>

									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											2.1 Tenant Screening Services:
										</b>{' '}
										TrueTenant is a technology provider and registered consumer
										reporting agency in Ontario. TrueTenant prepares and
										furnishes tenant-screening reports—including the TrueTenant
										Score—based on information you submit directly, information
										obtained through your authorized third-party data
										connections (e.g., Flinks, TenCheck, OpenRoom, Verrif), and
										information maintained in your TrueTenant tenant file.
										TrueTenant facilitates the delivery of screening reports,
										manages payments, and supports lease generation for
										landlords and property managers, and uses relevant collected
										information to produce Screening Services for your personal
										benefit and to furnish reports to landlords or property
										managers only with your explicit consent. Our platform also
										enables tenants and landlords/ property managers to
										communicate, exchange documents, and complete rental
										transactions through secure workflows.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">
											2.1.1 TrueTenant Acts as an independent technology
											provider
										</b>{' '}
										offering rental-application workflow tools; and a Consumer
										Reporting Agency (“CRA”) under the Ontario Consumer
										Reporting Act when it prepares and furnishes
										tenant-screening consumer reports and the TrueTenant Score
										for consideration in connection with tenancy decisions.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">2.1.2 </b>{' '}
										TrueTenant is not a real estate agent, property broker,
										lender, guarantor, insurer, or housing advisor, and does not
										own, manage, inspect, or control any property listed on the
										Platform. TrueTenant does not guarantee the condition,
										availability, or suitability of any property, nor the
										outcome of any tenant screening or rental decision.
										TrueTenant is not a credit bureau and does not maintain or
										furnish credit reports for lending or credit-granting
										purposes. However, as a CRA, TrueTenant maintains
										tenant-screening files and furnishes consumer reports under
										applicable law.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]">2.1.3. </b> Where
										Tenants list minors as occupants, they may only provide the
										minor’s age and relationship (e.g., child, sibling) and must
										not submit additional identifying data about the minor
										except as expressly required by TrueTenant and permitted by
										law.
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]"></b>{' '}
									</p>
									<p className="mb-4">
										<b className="font-[500] text-[#343434]"></b>{' '}
									</p>

									<h5 className="text-[16px] text-[#343434] mb-4  mt-4 font-[500]"></h5>
								</div>

								<div className="mt-6 flex justify-end gap-3">
									<button
										onClick={() => setShowTermsModal(false)}
										className="rounded-[20px] border px-4 py-2 text-sm"
									>
										Close
									</button>

									<button
										onClick={() => setShowTermsModal(false)}
										className="rounded-[20px] bg-[#32302F] px-4 py-2 text-sm text-white"
									>
										Accept
									</button>
								</div>
							</div>
						</div>
					)}
					<AnimatedContainer delay={0.3}>
						<AuthProviders
							type="Login"
							redirectTo={redirectTo}
							userType="tenant"
						/>
					</AnimatedContainer>
				</AnimatedContainer>
			</AuthFormWrapper>
		</AuthLayout>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Login to TrueTenant - Tenant' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
